// opervex-frontend/src/pages/Financial/Categories.js

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { FaPencilAlt, FaPlus, FaSave, FaSearch } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useParams } from "react-router-dom";
import { SearchHelpModal } from "../../utils/SearchHelp";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";

import useCommandActions, { DEFAULT_ACTIONS } from "../../hooks/useCommandActions";
import { useAuth } from "../../contexts/AuthContext";
import useOpervex from "../../hooks/useOpervex";

const rolePageRequire = "FINANCIAL_CATEGORIES";

const newDocument = () => ({
  id: "",
  code: "",
  description: "",
  disabled: false,
});

const Categories = () => {
  const { loadingGlobal } = useAuth();
  const { actions, commandAction } = useCommandActions(rolePageRequire);

  const { id } = useParams();
  const api = useOpervex();

  const [isCreate, setIsCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(newDocument());

  useEffect(() => {
    if (id) load(id);
    else commandAction({ ...DEFAULT_ACTIONS, create: true });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingGlobal) return <div>Carregando...</div>;

  // Load
  const load = async (recordId) => {
    setEditing(false);
    setIsCreate(false);

    if (!recordId) return;

    const result = await api.Financial.Categories.findId(recordId);
    if (result?.errors) return await ConfirmService.show("Aviso", result.errors[0]?.message || "Cliente não encontrado", true);

    if (result?.id) commandAction({ ...DEFAULT_ACTIONS, create: true, change: true });
    else commandAction({ ...DEFAULT_ACTIONS, create: true });

    setData(result);
  };

  // Update data object using screen
  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  // Handle Search
  const handleSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/Financial/Categories/SearchHelp",
      schema: await api.Financial.Categories.schema(),
    });

    if (item?.id) load(item.id);
  };

  // Handle Save
  const handleSave = async () => {
    const result = isCreate ? await api.Financial.Categories.create(data) : await api.Financial.Categories.update(data.id, data);
    if (result?.errors) return ConfirmService.show("Aviso", result.errors, true);

    await load(data.id || result.id);

    await ConfirmService.show("Aviso", "Registro salvo com sucesso!", true);
  };

  // Handle Create
  const handleCreate = async () => {
    setData(newDocument());
    setEditing(true);
    setIsCreate(true);

    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  // Handle Change
  const handleChange = async () => {
    setEditing(true);
    setIsCreate(false);
    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  // Handle Cancel
  const handleCancel = async () => {
    if (isCreate) {
      setData(newDocument());
      setEditing(false);
      commandAction({ ...DEFAULT_ACTIONS, create: true });
    } else load(data.id);
  };

  return (
    <Container className="mt-2">
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Categoria</h5>
            </Col>
            <Col className="text-end">
              <Button variant="success" size="sm" className="me-2" onClick={handleCreate} hidden={!actions.create}>
                <FaPlus /> Novo
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleChange} hidden={!actions.change}>
                <FaPencilAlt /> Alterar
              </Button>
              <Button variant="danger" size="sm" className="me-2" onClick={handleCancel} hidden={!actions.save}>
                <MdOutlineCancel /> Cancelar
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleSave} hidden={!actions.save}>
                <FaSave /> Salvar
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="g-2">
            <Col md={2}>
              <Form.Label>Código</Form.Label>
              <InputGroup>
                <Form.Control size="sm" value={data.id} disabled />
                <InputGroup.Text role="button" onClick={handleSearch}>
                  <FaSearch />
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Label>Tipo</Form.Label>
              <Form.Select size="sm" name="type" value={data.type || ""} onChange={change} disabled={!editing}>
                <option value="">Selecione</option>
                <option value="RECEIVABLE">(+) Recebível</option>
                <option value="PAYABLE">(-) Pagável</option>
                <option value="BOTH">(=) Ambos</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Descrição</Form.Label>
              <Form.Control size="sm" name="description" value={data.description} onChange={change} disabled={!editing} />
            </Col>
            <Col md={2} className="d-flex align-items-end pb-1">
              <Form.Check label="Inativo" name="disabled" checked={data.disabled} onChange={change} disabled={!editing} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Categories;
